import { Amplify } from "aws-amplify";
import { signIn, confirmSignIn } from '@aws-amplify/auth';
import config from '../../../amplifyconfiguration.json';

import { useContext, useState } from "react";
import { DataContext } from "../../../context/DataContext";
import { Link, useNavigate } from "react-router-dom";
import ErrorAlert from "../../../components/common/alerts/ErrorAlert";
import InputForm from "../../../components/form/InputForm";
import Loader from "../../../components/common/loaders/Loader";

Amplify.configure(config, {
  Routers: {
    routerName: "browserRouter",
    baseRedirect: "/index.html",
  },
});

const LoginForm = () => {
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Distinto de `error`/`loading` de arriba -- true mientras Cognito pide el
  // código TOTP (signIn() v6 devuelve {isSignedIn, nextStep} en vez de la
  // sesión directa de v5; antes este componente ignoraba nextStep por
  // completo y cualquier challenge de Cognito rompía el login en silencio).
  const [awaitingTotpCode, setAwaitingTotpCode] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const { setUserData } = useContext(DataContext);
  const navigate = useNavigate();

  const completeSignIn = (response) => {
    setUserData(response);
    // El access/id token real lo resuelve DataContext de forma independiente
    // vía fetchAuthSession() -- no hace falta (ni existe en Amplify v6)
    // response.signInUserSession.
    navigate("/layautapp");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await signIn({ username: email, password });
      if (response.isSignedIn) {
        completeSignIn(response);
        return;
      }
      switch (response.nextStep?.signInStep) {
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
          setAwaitingTotpCode(true);
          break;
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
          // Solo ocurriría si el User Pool exigiera TOTP (MfaConfiguration
          // "ON") sin que el usuario lo haya configurado antes -- fuera de
          // alcance hoy (el pool corre en OPTIONAL, el enrolamiento se hace
          // desde el perfil, ver ProfileDrawer.js).
          setError("Tu cuenta requiere configurar la verificación en dos pasos antes de continuar. Contacta a soporte.");
          break;
        default:
          setError("Flujo de inicio de sesión no soportado todavía.");
      }
    } catch (err) {
      if (err.code === "UserNotConfirmedException") {
        navigate("/confirm-user");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await confirmSignIn({ challengeResponse: totpCode });
      if (response.isSignedIn) {
        completeSignIn(response);
      } else {
        setError("Código incorrecto. Intenta de nuevo.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (awaitingTotpCode) {
    return (
      <div className="flex items-center justify-center px-6 py-8 min-h-screen bg-black text-white">
        <div className="w-full sm:max-w-md rounded-2xl border border-orange-500/60 bg-zinc-900/70 backdrop-blur shadow-xl">
          <div className="p-6 sm:p-8 space-y-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-center">
              <span className="text-white">Verificación en dos pasos</span>
              <span className="block mx-auto mt-2 h-1 w-16 bg-orange-500 rounded"></span>
            </h1>
            <p className="text-sm text-center text-gray-300">
              Ingresa el código de tu aplicación autenticadora.
            </p>
            <form className="space-y-5" onSubmit={handleTotpSubmit}>
              <InputForm
                name="totpCode"
                placeholder="123456"
                type="text"
                title="Código de verificación"
                value={totpCode}
                setValue={setTotpCode}
              />
              {error && <ErrorAlert error={error} />}
              <button
                className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 hover:bg-black/90 focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                type="submit"
              >
                Verificar
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-6 py-8 min-h-screen bg-black text-white">
      <div className="w-full sm:max-w-md rounded-2xl border border-orange-500/60 bg-zinc-900/70 backdrop-blur shadow-xl">
        <div className="p-6 sm:p-8 space-y-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-center">
            <span className="text-white">Iniciar Sesión</span>
            <span className="block mx-auto mt-2 h-1 w-16 bg-orange-500 rounded"></span>
          </h1>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <InputForm
              name="email"
              placeholder="Escribe tu correo electrónico"
              type="email"
              title="Correo electrónico"
              value={email}
              setValue={setEmail}
            />

            {/* Password */}
            <InputForm
              name="password"
              placeholder="Escribe tu contraseña"
              type="password"
              title="Contraseña"
              value={password}
              setValue={setPassword}
            />

            {/* Error */}
            {error && <ErrorAlert error={error} />}

            <div className="lg:flex items-center justify-between gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
                <input
                  id="remember"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-600 bg-black focus:ring-2 focus:ring-orange-500"
                />
                Recordar sesión
              </label>

              <Link
                to="/password/forgot-password"
                className="text-sm font-medium text-orange-500 hover:text-orange-400 transition"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 hover:bg-black/90 focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
              type="submit"
            >
              Entrar
            </button>

            <hr className="border-zinc-700" />

            <p className="text-sm text-center text-gray-300">
              ¿Aún no tienes cuenta?{" "}
              <Link
                to="/register"
                className="font-medium text-orange-500 hover:text-orange-400 transition"
              >
                Regístrate aquí
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
